import Box from "@mui/material/Box";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";

import Tooltip from "@mui/material/Tooltip";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Dialog from "@mui/material/Dialog";
import TimelineIcon from "@mui/icons-material/Timeline";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import DashboardIcon from "@mui/icons-material/Dashboard";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import CropFreeIcon from "@mui/icons-material/CropFree";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDrawPolygon } from "@fortawesome/free-solid-svg-icons";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import SwatchesPicker from "../Shared/SwatchesPicker";
import {
  actionDrawDefaultColorUpdated,
  actionDrawResetDefaultColor,
} from "./drawRedux";
import { actionMapMapviewChangeRequested } from "../Map";
import {
  actionScaleGridVisibleUpdated,
  SCALE_ZOOM_THRESHOLD,
} from "../ScaleGrid";
import { actionAddAlert, actionDeleteAlert } from "../Alert";

const button_ = (theme) => ({
  minWidth: 0,
  minHeight: 0,
  height: 34,
  width: 34,
  margin: "2px",
});

const icon_ = (theme) => ({
  fontSize: "1rem",
});

const arrow_ = (theme) => ({
  color: theme.palette.common.black,
});

const tooltip_ = (theme) => ({
  backgroundColor: theme.palette.common.black,
});

export function RectangleButton({ drawMode, clickCounter, changeMode }) {
  const MODE = "draw_rectangle";

  const dispatch = useDispatch();
  const { counter } = clickCounter;
  const { mode } = drawMode;
  const modeIsActive = mode === MODE;
  const onChangeMode = () => {
    changeMode(MODE);
  };

  let instruction = "";
  if (modeIsActive && counter < 1) {
    instruction = "Rectangle: Step 1 - Click on the Map";
  } else if (modeIsActive && counter >= 1) {
    instruction =
      "Rectangle: Step 2 - Move Your Mouse to Size the Rectangle, then Click to Complete";
  }

  useEffect(() => {
    if (modeIsActive) {
      dispatch(
        actionAddAlert({
          id: "draw",
          title: instruction,
          buttonLabel: "CANCEL",
          onClick: onChangeMode,
          onClose: onChangeMode,
        }),
      );
    }
  }, [modeIsActive, instruction, dispatch]);

  return (
    <Tooltip
      title="Rectangle"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <Button
        sx={button_}
        variant={modeIsActive ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={onChangeMode}
      >
        <CropFreeIcon />
      </Button>
    </Tooltip>
  );
}

export function LineButton({ drawMode, clickCounter, changeMode }) {
  const MODE = "draw_line_string";

  const dispatch = useDispatch();
  const { counter } = clickCounter;
  const { mode } = drawMode;
  const modeIsActive = mode === MODE;
  const onChangeMode = () => {
    changeMode(MODE);
  };

  let instruction = "";
  if (modeIsActive && counter < 1) {
    instruction = "Line: Click on the Map";
  } else if (modeIsActive && counter >= 1) {
    instruction = "Line: Double Click to Complete Line";
  }

  useEffect(() => {
    if (modeIsActive) {
      dispatch(
        actionAddAlert({
          id: "draw",
          title: instruction,
          buttonLabel: "CANCEL",
          onClick: onChangeMode,
          onClose: onChangeMode,
        }),
      );
    }
  }, [modeIsActive, instruction, dispatch]);

  return (
    <Tooltip
      title="Line"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <Button
        sx={button_}
        variant={modeIsActive ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={onChangeMode}
      >
        <TimelineIcon />
      </Button>
    </Tooltip>
  );
}

export function MarkerButton({ drawMode, changeMode }) {
  const MODE = "draw_point";

  const dispatch = useDispatch();
  const { mode } = drawMode;
  const modeIsActive = mode === MODE;
  const onChangeMode = () => {
    changeMode(MODE);
  };

  useEffect(() => {
    if (modeIsActive) {
      dispatch(
        actionAddAlert({
          id: "draw",
          title: "Marker: Click on the Map",
          buttonLabel: "CANCEL",
          onClick: onChangeMode,
          onClose: onChangeMode,
        }),
      );
    }
  }, [modeIsActive, dispatch]);

  return (
    <Tooltip
      title="Marker"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <Button
        sx={button_}
        variant={modeIsActive ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={onChangeMode}
      >
        <LocationOnIcon />
      </Button>
    </Tooltip>
  );
}

export function PolygonButton({ drawMode, clickCounter, changeMode }) {
  const MODE = "draw_polygon";

  const dispatch = useDispatch();
  const { counter } = clickCounter;
  const { mode } = drawMode;
  const modeIsActive = mode === MODE;
  const onChangeMode = () => {
    changeMode(MODE);
  };

  let instruction = "";
  if (modeIsActive && counter < 1) {
    instruction = "Polygon: Step 1 - Click on the Map";
  } else if (modeIsActive && counter === 1) {
    instruction = "Polygon: Step 2 - Click on another point";
  } else if (modeIsActive && counter > 1) {
    instruction = "Polygon: Double-Click to Complete Polygon";
  }

  useEffect(() => {
    if (modeIsActive) {
      dispatch(
        actionAddAlert({
          id: "draw",
          title: instruction,
          buttonLabel: "CANCEL",
          onClick: onChangeMode,
          onClose: onChangeMode,
        }),
      );
    }
  }, [modeIsActive, instruction, dispatch]);

  return (
    <Tooltip
      title="Polygon"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <Button
        sx={button_}
        variant={modeIsActive ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={onChangeMode}
      >
        <Box sx={icon_}>
          <FontAwesomeIcon icon={faDrawPolygon} />
        </Box>
      </Button>
    </Tooltip>
  );
}

export function RoomSelectButton({ drawMode, changeMode }) {
  const MODE = "room_picker";

  const dispatch = useDispatch();
  const roomStyle = { opacity: 0.75 };
  const pickerStyle = {
    position: "absolute",
    color: "black",
    top: 14,
    left: 11,
    fontSize: "1.3rem",
  };
  const { mode } = drawMode;
  const modeIsActive = mode === MODE;
  const onChangeMode = () => {
    changeMode(MODE, mode);
  };
  let instruction = "Room Picker: Click on a Room";

  useEffect(() => {
    if (modeIsActive) {
      dispatch(
        actionAddAlert({
          id: "draw",
          title: instruction,
          buttonLabel: "CANCEL",
          onClick: onChangeMode,
          onClose: onChangeMode,
        }),
      );
    }
  }, [modeIsActive, instruction, dispatch]);

  return (
    <Tooltip
      title="Room Picker"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <Button
        sx={button_}
        variant={modeIsActive ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={onChangeMode}
      >
        <DashboardIcon style={roomStyle} />
        <TouchAppIcon style={pickerStyle} />
      </Button>
    </Tooltip>
  );
}

export function GroupRoomSelectButton({ drawMode, changeMode }) {
  const MODE = "group_room_picker";

  const dispatch = useDispatch();
  const roomStyle = { opacity: 0.75 };
  const pickerStyle = {
    position: "absolute",
    color: "black",
    top: -3,
    left: -3,
    fontSize: "2.4rem",
  };
  const { mode } = drawMode;
  const modeIsActive = mode === MODE;
  const onChangeMode = () => {
    changeMode(MODE, mode);
  };
  let instruction = "Room Picker: Draw Rectangle to Select Multiple Rooms";

  useEffect(() => {
    if (modeIsActive) {
      dispatch(
        actionAddAlert({
          id: "draw",
          title: instruction,
          buttonLabel: "CANCEL",
          onClick: onChangeMode,
          onClose: onChangeMode,
        }),
      );
    }
  }, [modeIsActive, instruction, dispatch]);

  return (
    <Tooltip
      title="Group Room Picker"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <Button
        sx={button_}
        variant={modeIsActive ? "contained" : "outlined"}
        color="primary"
        size="small"
        onClick={onChangeMode}
      >
        <DashboardIcon style={roomStyle} />
        <CropFreeIcon style={pickerStyle} />
      </Button>
    </Tooltip>
  );
}

export function DeleteAllButton({ featureGroups, removeAll }) {
  const visible =
    featureGroups &&
    featureGroups.length &&
    featureGroups[0].features &&
    featureGroups[0].features.length;
  const style = { margin: "2px 2px 2px 35px", height: 34 };

  return (
    <Tooltip
      describeChild
      title="Remove All Objects"
      placement="top"
      slotProps={{
        arrow: {
          sx: arrow_,
        },
        tooltip: {
          sx: tooltip_,
        },
      }}
    >
      <span style={{ visibility: visible ? "visible" : "hidden" }}>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          style={style}
          onClick={removeAll}
        >
          Clear
        </Button>
      </span>
    </Tooltip>
  );
}

export function MoreToolsButton({ moreToolsVisibile, moreToolsClick }) {
  const style = { padding: "0px", minWidth: 0 };
  const title = moreToolsVisibile ? "Hide Tools" : "Show More Tools";
  const icon = moreToolsVisibile ? <ExpandLessIcon /> : <ExpandMoreIcon />;
  const onClick = () => {
    moreToolsClick();
  };

  return (
    <Tooltip title={title}>
      <Button style={style} onClick={onClick}>
        {icon}
      </Button>
    </Tooltip>
  );
}

const root_ = (theme) => ({
  margin: "4px 20px 4px auto",
});

const display_ = (theme) => ({
  width: 20,
  height: 20,
  borderRadius: "50%",
  "&:hover": {
    cursor: "pointer",
  },
});

export function ColorPicker() {
  const dispatch = useDispatch();
  const defaultColor = useSelector((state) => state.drawReducer.defaultColor);
  const bgColor = { backgroundColor: defaultColor };
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const handleChange = (color) => {
    dispatch(actionDrawDefaultColorUpdated(color.hex));
  };
  const handleDblClick = () => {
    dispatch(actionDrawResetDefaultColor());
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={root_}>
        <Box
          style={bgColor}
          sx={display_}
          onClick={handleClick}
          onDoubleClick={handleDblClick}
        ></Box>
        <Dialog onClose={handleClose} open={open}>
          <SwatchesPicker
            color={defaultColor}
            onChangeComplete={handleChange}
          />
        </Dialog>
      </Box>
    </ClickAwayListener>
  );
}

export const GridToggle = () => {
  const dispatch = useDispatch();
  const { gridVisible: gridToggleOn } = useSelector(
    (state) => state.scaleReducer,
  );
  const { pitch, zoom } = useSelector((state) => state.mapReducer);
  const gridVisible = gridToggleOn && zoom >= SCALE_ZOOM_THRESHOLD;

  const cancelGrid = () => {
    dispatch(actionScaleGridVisibleUpdated({ gridVisible: false }));
  };

  const handleChange = (event) => {
    dispatch(
      actionScaleGridVisibleUpdated({
        gridVisible: event.target.checked,
      }),
    );
  };

  const resetPitch = () => {
    dispatch(
      actionMapMapviewChangeRequested({
        pitch: 0,
      }),
    );
  };

  const setZoom = () => {
    dispatch(
      actionMapMapviewChangeRequested({
        zoom: SCALE_ZOOM_THRESHOLD,
      }),
    );
  };

  useEffect(() => {
    if (gridVisible) {
      dispatch(
        actionAddAlert({
          id: "grid",
          title: `1 ft by 1 ft scale grid overlay`,
          buttonLabel: "REMOVE",
          onClick: cancelGrid,
          onClose: cancelGrid,
        }),
      );
      dispatch(actionDeleteAlert("gridzoomwarn"));
    } else if (gridToggleOn && !gridVisible) {
      dispatch(
        actionAddAlert({
          id: "gridzoomwarn",
          severity: "warning",
          title: `1 ft by 1 ft grid not visible at this zoom level`,
          buttonLabel: "Zoom In",
          onClick: setZoom,
        }),
      );
      dispatch(actionDeleteAlert("grid"));
    } else {
      dispatch(actionDeleteAlert("grid"));
      dispatch(actionDeleteAlert("gridzoomwarn"));
    }
  }, [gridVisible, gridToggleOn]);

  useEffect(() => {
    if (pitch && gridVisible) {
      dispatch(
        actionAddAlert({
          id: "gridpitchwarn",
          severity: "warning",
          title: `1 ft by 1 ft grid not to scale while map is pitched`,
          buttonLabel: "RESET PITCH",
          onClick: resetPitch,
          onClose: resetPitch,
        }),
      );
    } else {
      dispatch(actionDeleteAlert("gridpitchwarn"));
    }
  }, [pitch, gridVisible]);

  return (
    <Tooltip title="Overlay 1 ft by 1 ft Scale Grid" placement="top">
      <FormControlLabel
        control={
          <Switch
            checked={gridToggleOn}
            color="primary"
            onChange={handleChange}
            name="Grid"
          />
        }
        label="Grid"
        labelPlacement="start"
      />
    </Tooltip>
  );
};
